import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, imgUrl } from "../../lib/api";
import { toast } from "sonner";
import { BadgeCheck, Store, Instagram, Youtube, Globe, Users, Film, ArrowLeft } from "lucide-react";
import VideoEmbed from "../../components/store/VideoEmbed";
import { useAuth } from "../../context/AuthContext";
import { useBranding } from "../../context/BrandingContext";

const PLATFORM_ICON = { instagram: Instagram, youtube: Youtube };

export default function CreatorPage() {
  const { handle } = useParams();
  const { user } = useAuth();
  const { branding } = useBranding();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const { data } = await api.get(`/creators/${handle}`);
        if (!alive) return;
        setCreator(data);
        setFollowing(Boolean(data?.is_following));
        document.title = `${data.display_name} · ${branding.brand_name} Reels`;
      } catch (e) {
        if (!alive) return;
        if (e?.response?.status === 404) setError("Creator not found");
        else setError(e?.response?.data?.detail || "Could not load creator");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; document.title = branding.brand_name; };
  }, [handle, branding.brand_name]);

  const toggleFollow = async () => {
    if (!user) {
      toast.error("Sign in to follow creators");
      return;
    }
    const next = !following;
    setBusy(true);
    try {
      if (next) await api.post(`/creators/${handle}/follow`);
      else await api.delete(`/creators/${handle}/follow`);
      setFollowing(next);
      setCreator((c) => c ? { ...c, follower_count: (c.follower_count || 0) + (next ? 1 : -1) } : c);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update follow");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="px-6 md:px-12 lg:px-24 py-20">
        <div className="h-40 bg-[hsl(var(--kb-surface-alt))] animate-pulse mb-6" />
        <div className="h-10 w-60 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
      </div>
    );
  }

  if (error || !creator) {
    return (
      <div className="px-6 md:px-12 lg:px-24 py-20 text-center">
        <h1 className="font-display text-4xl mb-4">Creator not found</h1>
        <Link to="/reels" className="kb-btn-outline inline-flex items-center gap-2">
          <ArrowLeft size={14} /> Back to Style Feed
        </Link>
      </div>
    );
  }

  const reels = creator.reels || [];

  return (
    <div data-testid="creator-page" className="bg-[hsl(var(--kb-surface))]">
      {/* Banner / avatar hero */}
      <section className="relative">
        <div className="h-48 md:h-64 bg-[hsl(var(--kb-primary))] overflow-hidden">
          {creator.banner_url && (
            <img src={imgUrl(creator.banner_url)} alt="" className="w-full h-full object-cover opacity-70" />
          )}
        </div>
        <div className="px-6 md:px-12 lg:px-24 -mt-14 relative z-10 flex flex-col md:flex-row md:items-end gap-6">
          <div className="flex items-end gap-5">
            <div className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden bg-white ring-4 ring-white shrink-0">
              {creator.avatar_url ? (
                <img src={imgUrl(creator.avatar_url)} alt={creator.display_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-[hsl(var(--kb-surface-alt))]" />
              )}
            </div>
            <div className="pb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-3xl md:text-4xl leading-tight">{creator.display_name}</h1>
                {creator.is_verified && (
                  <BadgeCheck size={22} className="text-[hsl(var(--kb-primary))]" />
                )}
              </div>
              <div className="text-sm text-[hsl(var(--kb-muted))]">@{creator.handle}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 md:ml-auto pb-2">
            <button
              onClick={toggleFollow}
              disabled={busy}
              data-testid="creator-page-follow-btn"
              className={`px-6 py-2.5 text-xs uppercase tracking-widest border transition-colors ${
                following
                  ? "bg-white text-[hsl(var(--kb-text))] border-[hsl(var(--kb-border))]"
                  : "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]"
              }`}
            >
              {following ? "Following" : "Follow"}
            </button>
            {creator.linked_seller?.shop_slug && (
              <Link
                to={`/shop/${creator.linked_seller.shop_slug}`}
                className="kb-btn-outline text-xs"
                data-testid="creator-page-visit-atelier"
              >
                <Store size={12} className="mr-1.5" /> Visit atelier
              </Link>
            )}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-10">
        <div className="grid md:grid-cols-[280px_1fr] gap-10">
          {/* Sidebar — bio, counts, social */}
          <aside className="space-y-6">
            {creator.bio && (
              <p className="text-sm text-[hsl(var(--kb-text))] leading-relaxed">{creator.bio}</p>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-1.5 overline mb-1"><Users size={12} /> Followers</div>
                <div className="font-display text-2xl" data-testid="creator-follower-count">
                  {(creator.follower_count || 0).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 overline mb-1"><Film size={12} /> Reels</div>
                <div className="font-display text-2xl">{(creator.reel_count || reels.length).toLocaleString()}</div>
              </div>
            </div>
            {Array.isArray(creator.social_handles) && creator.social_handles.length > 0 && (
              <div className="space-y-2">
                <div className="overline mb-1">Find us on</div>
                {creator.social_handles.map((s, i) => {
                  const Icon = PLATFORM_ICON[s.platform] || Globe;
                  return (
                    <a
                      key={i}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm hover:text-[hsl(var(--kb-primary))]"
                    >
                      <Icon size={14} /> <span className="truncate">{s.url.replace(/^https?:\/\//, "")}</span>
                    </a>
                  );
                })}
              </div>
            )}
            {Array.isArray(creator.tags) && creator.tags.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {creator.tags.map((t) => (
                  <span key={t} className="text-[11px] uppercase tracking-widest px-2 py-1 bg-[hsl(var(--kb-surface-alt))]">
                    #{t}
                  </span>
                ))}
              </div>
            )}
          </aside>

          {/* Reels grid */}
          <div>
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="overline mb-1">Their reels</div>
                <h2 className="font-display text-3xl">Latest content</h2>
              </div>
            </div>
            {reels.length === 0 ? (
              <div className="py-16 text-center text-sm text-[hsl(var(--kb-muted))]">
                No reels from this creator yet.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {reels.map((r) => (
                  <Link
                    key={r.id}
                    to={`/reels?content=${r.id}`}
                    data-testid={`creator-reel-${r.id}`}
                    className="block"
                  >
                    <VideoEmbed content={r} aspect="9x16" testid={`creator-video-${r.id}`} />
                    <div className="mt-2 text-sm font-medium line-clamp-2">{r.title}</div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
